/*
 * Cliente del incremento 20/21 · Préstamos: alta y pagos.
 *
 * Usa la fábrica del núcleo (crearCliente) para NO reimplementar la cookie de
 * sesión, la envoltura común de respuesta ni el aviso de fin de sesión al shell.
 *
 * `fechaLocal` es OBLIGATORIA en todo: sin ella el servidor no puede derivar si
 * una mensualidad está pendiente o cargada, ni la etiqueta del periodo, y NUNCA la
 * suple con su reloj (D-06, RN-104b).
 *
 * REGLA DURA DE TODO EL MÓDULO (RNF-038, RNF-138): aquí no se arma ningún
 * contador, no se divide ningún importe y no se calcula ninguna fecha. El «15/48»,
 * el reparto propuesto de capital e interés, el pago esperado del periodo, su
 * etiqueta y el calendario completo viajan YA RESUELTOS del servidor. Si aparece
 * una interpolación que arme un contador o un `AddMonths` disfrazado en este
 * módulo, es defecto de construcción.
 *
 * EL ALTA DE LA CUENTA PRÉSTAMO NO ESTÁ AQUÍ: la hace el 2/6 con su propia ruta y
 * el tipo nuevo. Son DOS LLAMADAS —alta de cuenta y generación del plan— y si la
 * segunda falla, la cuenta queda bien con su deuda (pantalla «sin plan»).
 */

import { crearCliente } from '../../../nucleo/api/cliente.js';

const api = crearCliente('/api/prestamos');

/**
 * Los CUATRO estados del periodo de un préstamo (D-438, RN-749). Se DERIVAN al
 * consultar contra el pago esperado —la mensualidad— y jamás contra la deuda
 * total; nada de esto está guardado (RNF-137).
 */
export const ESTADOS_DEL_PERIODO = {
  porVencer: 'POR_VENCER',
  parcial: 'PARCIAL',
  pagada: 'PAGADA',
  vencida: 'VENCIDA',
};

/** Estados presentados de un pago del calendario, derivados por fecha (RN-104b). */
export const ESTADOS_DE_PAGO = {
  pendiente: 'PENDIENTE',
  cargada: 'CARGADA',
  cancelada: 'CANCELADA',
  liquidada: 'LIQUIDADA',
};

/**
 * ConsultarPrestamo (§4, RF-270). Trae la cuenta con su deuda, el plan, el
 * calendario COMPLETO —el pliegue por año es presentación (RN-776)— y el
 * historial de pagos con su desglose derivado de los movimientos (D-435).
 *
 * `planGenerado: false` NO ES ERROR (RN-778): el préstamo existe con su deuda y
 * los pagos se registran igual.
 */
export function obtenerPrestamo({ idCuenta, fechaLocal }) {
  return api.obtener(`/${idCuenta}?fechaLocal=${encodeURIComponent(fechaLocal)}`);
}

/**
 * SimularPlanDePagosDePrestamo (§2, patrón D-78/D-413). SOLO LECTURA: no escribe
 * nada y usa la MISMA preparación que la generación, así que la vista previa y lo
 * que se guarda no pueden diferir.
 */
export function simularPlan({ idCuenta, ...datos }) {
  return api.publicar(`/${idCuenta}/plan/simulacion`, { idCuenta, ...datos });
}

/**
 * GenerarPlanDePagosDePrestamo (§1, RF-265). Transaccional. NO crea ningún
 * movimiento y NO cambia ninguna cifra de deuda (CA-20-06).
 */
export function generarPlan({ idCuenta, ...datos }) {
  return api.publicar(`/${idCuenta}/plan`, { idCuenta, ...datos });
}

/**
 * La PRECARGA del pago (D-430, RN-745): el reparto propuesto y, dicho con sus
 * palabras, de dónde sale o de dónde NO pudo salir. La propuesta nunca bloquea.
 */
export function obtenerPrecargaDePago({ idCuenta, fechaLocal }) {
  return api.obtener(`/${idCuenta}/pagos/precarga?fechaLocal=${encodeURIComponent(fechaLocal)}`);
}

/**
 * RegistrarPagoDePrestamo (§3, RF-267, D-435). UNA transacción: el traspaso del
 * capital, el gasto del interés y el vínculo. NO exige plan generado (RN-778).
 */
export function registrarPago({ idCuenta, ...datos }) {
  return api.publicar(`/${idCuenta}/pagos`, datos);
}

/**
 * CancelarPlanDePagosDePrestamo (§5, D-440). Las filas se conservan canceladas;
 * movimientos y deuda quedan intactos. Sin `confirmado` el servidor responde
 * CONFIRMACION_REQUERIDA con el efecto explicado. No existe descancelar.
 */
export function cancelarPlan({ idCuenta, ...datos }) {
  return api.actualizar(`/${idCuenta}/plan/cancelacion`, datos);
}

// ---------------------------------------------------------------------------
// Incremento 21/21 · PAGO ADELANTADO A CAPITAL (contratos rev. 4, §9 y §10)
// ---------------------------------------------------------------------------

/**
 * Los DOS modos que la persona elige (D-463, D-465). `LIQUIDACION` NO está aquí a
 * propósito: no se captura, lo pone el servidor cuando el abono iguala la deuda
 * (D-468). Ofrecerlo sería pedirle que decida algo que no decide.
 */
export const MODOS_DEL_ADELANTO = {
  reducirPlazo: 'REDUCIR_PLAZO',
  reducirMensualidad: 'REDUCIR_MENSUALIDAD',
};

/**
 * SimularPagoAdelantadoDePrestamo (§9 rev. 4, D-463/D-466/D-472). SOLO LECTURA:
 * cero filas, cero bitácora, idempotente — por eso se puede volver a llamar cada
 * vez que la persona corrige la cifra, que es justo lo que RN-795a manda.
 *
 * LA CIFRA CORREGIDA VIAJA COMO ENTRADA OPCIONAL: sin ella el servidor devuelve su
 * propuesta; con ella, el calendario y el resumen se derivan de ELLA, para que lo
 * que se enseña no pueda contradecir a lo que se va a guardar (CA-21-05/CA-21-21).
 * La pantalla NO calcula nada (RNF-038/RNF-138): re-pregunta.
 */
export function simularPagoAdelantado({ idCuenta, ...datos }) {
  return api.publicar(`/${idCuenta}/pagos-adelantados/simulacion`, datos);
}

/**
 * RegistrarPagoAdelantadoDePrestamo (§10, RF-276, D-464, D-465). UNA transacción:
 * el traspaso del abono, el ajuste del plan, el recálculo de las pendientes según
 * el modo y el vínculo.
 *
 * `confirmado` es OBLIGATORIO (RN-784f): el registro reescribe el calendario y no
 * se des-aplica. Sin él el servidor responde CONFIRMACION_REQUERIDA con el efecto
 * explicado, y hace bien: la salvaguarda no puede depender de que esta pantalla se
 * acuerde de pedirla.
 */
export function registrarPagoAdelantado({ idCuenta, ...datos }) {
  return api.publicar(`/${idCuenta}/pagos-adelantados`, datos);
}
